import { forwardRef, useMemo } from "react";
import type { CodeBlockProps, BlockType, BlockTypeConfig, Direction } from "./types";

/**
 * Block type configurations with visual properties
 */
export const BLOCK_CONFIGS: Record<BlockType, BlockTypeConfig> = {
  move_up: {
    type: "move_up",
    label: "Up",
    icon: "↑",
    color: "direction",
    direction: "up",
  },
  move_down: {
    type: "move_down",
    label: "Down",
    icon: "↓",
    color: "direction",
    direction: "down",
  },
  move_left: {
    type: "move_left",
    label: "Left",
    icon: "←",
    color: "direction",
    direction: "left",
  },
  move_right: {
    type: "move_right",
    label: "Right",
    icon: "→",
    color: "direction",
    direction: "right",
  },
  repeat: {
    type: "repeat",
    label: "Repeat",
    icon: "🔄",
    color: "loop",
  },
};

/**
 * Get the direction from a block type
 */
export function getBlockDirection(type: BlockType): Direction | null {
  return BLOCK_CONFIGS[type].direction ?? null;
}

/**
 * CodeBlock Component
 *
 * A draggable code block element for the visual programming interface.
 * Designed with a playful tech-toy aesthetic matching Pixel's copper/orange theme.
 * Features chunky tactile appearance with satisfying visual feedback.
 */
export const CodeBlock = forwardRef<HTMLDivElement, CodeBlockProps>(
  (
    {
      block,
      isDragging = false,
      isPalette = false,
      onDelete,
      disabled = false,
      showDelete = false,
      isExecuting = false,
    },
    ref
  ) => {
    const config = BLOCK_CONFIGS[block.type];
    const isLoop = config.color === "loop";

    // Color schemes for different block types
    const colorStyles = useMemo(() => {
      if (isLoop) {
        // Loop blocks: Vibrant green with cyan accents
        return {
          bg: "from-emerald-400 via-emerald-500 to-emerald-600",
          border: "border-emerald-600",
          shadow: "shadow-[0_4px_0_0_theme(colors.emerald.700)]",
          hoverShadow: "hover:shadow-[0_5px_0_0_theme(colors.emerald.700)]",
          glow: "rgba(16, 185, 129, 0.4)",
          iconBg: "bg-emerald-300/50",
          accent: "#10B981",
        };
      }
      // Direction blocks: Pixel's warm copper/sky blue palette
      return {
        bg: "from-sky-400 via-sky-500 to-sky-600",
        border: "border-sky-600",
        shadow: "shadow-[0_4px_0_0_theme(colors.sky.700)]",
        hoverShadow: "hover:shadow-[0_5px_0_0_theme(colors.sky.700)]",
        glow: "rgba(56, 189, 248, 0.4)",
        iconBg: "bg-sky-300/50",
        accent: "#38BDF8",
      };
    }, [isLoop]);

    return (
      <div
        ref={ref}
        className={`
          group relative
          select-none touch-manipulation
          transition-all duration-200 ease-out
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-grab active:cursor-grabbing"}
          ${isDragging ? "scale-105 rotate-2 z-50" : ""}
          ${isPalette ? "hover:-translate-y-1" : ""}
          ${isExecuting ? "scale-110 z-20" : ""}
        `}
        role="button"
        aria-label={`${config.label} block${isPalette ? ", drag to add" : ""}${isExecuting ? ", currently executing" : ""}`}
        tabIndex={disabled ? -1 : 0}
      >
        {/* Executing glow ring */}
        {isExecuting && (
          <div
            className="
              absolute -inset-1
              rounded-3xl
              bg-sunshine-400
              animate-pulse
              opacity-60
            "
            style={{ animationDuration: "0.5s" }}
          />
        )}

        {/* Main block body */}
        <div
          className={`
            relative flex items-center gap-3
            min-h-[64px] px-4 py-3
            bg-gradient-to-b ${colorStyles.bg}
            ${colorStyles.border} border-[3px]
            rounded-2xl
            ${colorStyles.shadow}
            ${!disabled && !isDragging ? colorStyles.hoverShadow : ""}
            ${isDragging ? "shadow-[0_8px_24px_rgba(0,0,0,0.25)]" : ""}
            ${isExecuting ? "shadow-[0_0_20px_rgba(255,217,61,0.6),0_4px_0_0_theme(colors.sky.700)]" : ""}
            transition-all duration-150
            overflow-hidden
          `}
        >
          {/* Circuit pattern overlay */}
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute top-1/4 left-0 right-0 h-[1px] bg-white/40" />
            <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-white/30" />
            <div className="absolute top-3/4 left-0 right-0 h-[1px] bg-white/20" />
            {/* Vertical circuit lines */}
            <div className="absolute left-1/4 top-0 bottom-0 w-[1px] bg-white/20" />
            <div className="absolute right-1/4 top-0 bottom-0 w-[1px] bg-white/20" />
          </div>

          {/* Connector nubs (left) - puzzle-piece style */}
          <div
            className={`
              absolute -left-1 top-1/2 -translate-y-1/2
              w-3 h-6
              bg-gradient-to-r ${colorStyles.bg}
              rounded-r-full
              border-r-2 border-t-2 border-b-2 ${colorStyles.border}
            `}
          />

          {/* Connector nubs (right) */}
          <div
            className={`
              absolute -right-1 top-1/2 -translate-y-1/2
              w-3 h-6
              bg-gradient-to-l ${colorStyles.bg}
              rounded-l-full
              border-l-2 border-t-2 border-b-2 ${colorStyles.border}
            `}
          />

          {/* Icon container with gear decoration */}
          <div
            className={`
              relative flex items-center justify-center
              w-12 h-12
              ${colorStyles.iconBg}
              rounded-xl
              border-2 border-white/30
              shadow-inner
            `}
          >
            {/* Rotating gear behind icon (for loop blocks) */}
            {isLoop && (
              <div className="absolute inset-[-4px] text-2xl opacity-30 animate-spin-slow pointer-events-none">
                ⚙️
              </div>
            )}

            {/* Arrow icon with direction styling */}
            <span
              className={`
                relative z-10
                text-2xl font-bold
                ${isLoop ? "" : "font-mono"}
                text-white
                drop-shadow-[0_2px_2px_rgba(0,0,0,0.3)]
                ${config.direction === "up" ? "transform -translate-y-0.5" : ""}
                ${config.direction === "down" ? "transform translate-y-0.5" : ""}
              `}
            >
              {config.icon}
            </span>

            {/* Glowing ring for direction blocks */}
            {!isLoop && (
              <div
                className={`
                  absolute inset-0 rounded-xl
                  ring-2 ring-white/20
                  animate-pulse
                `}
                style={{ animationDuration: "2s" }}
              />
            )}
          </div>

          {/* Label */}
          <span
            className="
              relative z-10
              font-display font-bold text-xl
              text-white
              tracking-wide
              drop-shadow-[0_2px_2px_rgba(0,0,0,0.2)]
            "
          >
            {config.label}
          </span>

          {/* Repeat count badge (for loop blocks) */}
          {isLoop && block.repeatCount && (
            <div
              className="
                flex items-center justify-center
                min-w-[36px] h-9
                px-2
                bg-white/90
                rounded-lg
                border-2 border-emerald-600
                shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]
              "
            >
              <span className="font-display font-extrabold text-lg text-emerald-600">
                {block.repeatCount}x
              </span>
            </div>
          )}

          {/* Metallic bolt decorations */}
          <div
            className="
              absolute top-2 right-2
              w-2.5 h-2.5 rounded-full
              bg-gradient-to-br from-neutral-300 via-neutral-400 to-neutral-500
              border border-neutral-500
              shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]
            "
          />
          <div
            className="
              absolute bottom-2 right-2
              w-2.5 h-2.5 rounded-full
              bg-gradient-to-br from-neutral-300 via-neutral-400 to-neutral-500
              border border-neutral-500
              shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]
            "
          />

          {/* Shine effect */}
          <div
            className="
              absolute inset-x-0 top-0 h-1/2
              bg-gradient-to-b from-white/20 to-transparent
              rounded-t-xl
              pointer-events-none
            "
          />
        </div>

        {/* Delete button */}
        {showDelete && onDelete && !disabled && !isPalette && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="
              absolute -top-2 -right-2
              flex items-center justify-center
              w-8 h-8
              bg-gradient-to-b from-fairy-400 to-fairy-500
              rounded-full
              border-2 border-fairy-600
              shadow-[0_2px_0_0_theme(colors.fairy.600)]
              opacity-0 group-hover:opacity-100
              transition-all duration-150
              hover:scale-110 hover:-translate-y-0.5
              active:scale-95 active:translate-y-0.5
              active:shadow-[0_0px_0_0_theme(colors.fairy.600)]
              focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white
            "
            aria-label={`Delete ${config.label} block`}
          >
            <span className="text-white font-bold text-lg leading-none">×</span>
          </button>
        )}

        {/* Drag hint indicator (for palette blocks) */}
        {isPalette && !disabled && (
          <div
            className="
              absolute -bottom-1 left-1/2 -translate-x-1/2
              flex items-center gap-1
              px-2 py-0.5
              bg-neutral-800/80
              rounded-full
              text-[10px] font-body text-white/80
              opacity-0 group-hover:opacity-100
              transition-opacity duration-200
              pointer-events-none
            "
          >
            <span className="animate-bounce" style={{ animationDuration: "1s" }}>
              ↓
            </span>
            drag
          </div>
        )}
      </div>
    );
  }
);

CodeBlock.displayName = "CodeBlock";

export default CodeBlock;
